import { Color } from '@persona-id/design-tokens';
import {
  ChangeEvent,
  FocusEvent,
  FocusEventHandler,
  KeyboardEventHandler,
  MutableRefObject,
  useState,
} from 'react';
import styled from 'styled-components';

import Pill from 'dashboard/components/FormControlPilledInput/Pill';
import { ControlConfig, processTextValue } from 'dashboard/components/FormControlPilledInput/types';
import { TextValue, ValueType } from 'dashboard/models/graph-query';

interface EditableItemVariableInputProps {
  controlConfig: ControlConfig;
  deleteItem: () => void;
  deleteLastItem: () => void;
  inputRef: MutableRefObject<HTMLInputElement>;
  isLast: boolean;
  itemKey: string;
  placeholder: string | null;
  value: TextValue;
  onValueChange: (value: TextValue) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  isCompact?: boolean;
  isVariable?: boolean;
}

export const EditableInput = styled.input`
  background: ${Color.CoreSlate100};
`;

const EditableItemVariableInput = ({
  deleteItem,
  deleteLastItem,
  inputRef,
  isLast,
  itemKey,
  onValueChange,
  onBlur,
  placeholder,
  value: initialValue,
  // Remove this flag once we've ramped GraphV2 to 100%
  isCompact = false,
}: EditableItemVariableInputProps) => {
  const [value, setValue] = useState<string>(initialValue['value']);
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const commitValue = () => {
    const processedValue = processTextValue({
      ...initialValue,
      'raw-value': `{{${initialValue['name']}: ${value}}}`,
      type: ValueType.Text,
    });

    onValueChange(processedValue);

    // TODO: See if we can remove this by changing 'defaultValue' and
    // making use of useEffect
    setValue('');
  };

  const handleKeyPress: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter' && value.trim() !== '') {
      e.preventDefault();
      commitValue();
    }
  };

  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Tab' && value.trim() !== '') {
      e.preventDefault();

      commitValue();
    }
  };

  const handleClearClick = () => {
    if (isLast) {
      deleteLastItem();
    } else {
      deleteItem();
    }
  };

  const handleBlur: FocusEventHandler = (e: FocusEvent<HTMLInputElement>) => {
    if (initialValue['value'] !== value) {
      commitValue();
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <Pill isCompact={isCompact} color='gray' handleClear={handleClearClick} clearable>
      <>
        <b>{initialValue['name']}</b>
        :&nbsp;
        <EditableInput
          key={itemKey}
          size={(value?.length || 1) * 1.15}
          ref={(input) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            inputRef.current = input;
          }}
          value={value}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          placeholder={placeholder}
        />
      </>
    </Pill>
  );
};

export default EditableItemVariableInput;
